import { getAccessToken, getCsrfToken } from "./authClient";

let tokenProvider = null;

export function setAuthTokenProvider(fn) {
    tokenProvider = typeof fn === "function" ? fn : null;
}

export function getBaseUrl() {
    // In production an empty base URL means relative paths via reverse proxy.
    const base = import.meta.env.VITE_CONTENT_API_URL ?? "";
    return base.replace(/\/$/, "");
}

function buildQuery(params) {
    if (!params) return "";

    // URLSearchParams encodes `:` as `%3A` in keys, but the SbContent API uses
    // literal colons in filter operators (e.g. filter.systemName:contains).
    // Build the query string manually to preserve `:` and `.` in key names.
    const parts = [];
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        const encKey = encodeURIComponent(k).replace(/%3A/gi, ":").replace(/%2E/gi, ".");
        if (Array.isArray(v)) {
            v.forEach((x) => parts.push(`${encKey}[]=${encodeURIComponent(x)}`));
        } else {
            parts.push(`${encKey}=${encodeURIComponent(v)}`);
        }
    });

    return parts.length ? `?${parts.join("&")}` : "";
}

async function resolveAccessToken() {
    const provided = tokenProvider ? tokenProvider() : null;
    const candidate = provided && typeof provided.then === "function"
        ? await provided
        : provided;

    if (typeof candidate === "string" && candidate.trim()) {
        return candidate.trim();
    }

    const fallback = getAccessToken();
    if (typeof fallback === "string" && fallback.trim()) {
        return fallback.trim();
    }

    return null;
}

export async function contentFetch(path, { method = "GET", params, body, headers, signal } = {}) {
    const url = `${getBaseUrl()}${path}${buildQuery(params)}`;
    const token = await resolveAccessToken();

    const mutatingMethods = ["POST", "PUT", "PATCH", "DELETE"];
    const csrfToken = mutatingMethods.includes(method.toUpperCase()) ? getCsrfToken() : null;

    const requestHeaders = new Headers(headers || {});

    if (body && !requestHeaders.has("Content-Type")) {
        requestHeaders.set("Content-Type", "application/json");
    }

    if (token && !requestHeaders.has("Authorization")) {
        requestHeaders.set("Authorization", `Bearer ${token}`);
    }

    if (csrfToken && !requestHeaders.has("X-CSRF-Token")) {
        requestHeaders.set("X-CSRF-Token", csrfToken);
    }

    const res = await fetch(url, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal,
        credentials: "include",
    });

    if (!res.ok) {
        const text = await res.text().catch(() => "");
        const msg = text || `${res.status} ${res.statusText}`;
        const error = new Error(`[SbContentAPI] ${msg}`);
        error.status = res.status;

        // Глобальный toast для серверных/неожиданных ошибок content-api.
        // 401 пропускаем — это обработает auth-слой (refresh / redirect).
        if (res.status !== 401) {
            try {
                const { notify } = await import("shared/utils/notify");
                notify.error(humanizeContentError(res.status, text));
            } catch {
                /* notify недоступен — игнорируем */
            }
        }

        throw error;
    }

    if (res.status === 204) return null;

    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) return res.json();
    return res.text();
}

function humanizeContentError(status, text) {
    if (status >= 500) return "Сервер контента недоступен. Попробуйте позже.";
    if (status === 429) return "Слишком много запросов. Подождите и повторите.";
    if (status === 403) return "Недостаточно прав для выполнения операции.";
    if (status === 404) return "Запрашиваемые данные не найдены.";
    if (status === 400 && text) {
        // Часто backend кладёт сообщение в text — пробуем JSON parse.
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) return parsed.message;
        } catch {
            /* not json */
        }
    }
    return `Ошибка запроса (${status}).`;
}